'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { createCompanyChannel, type SyncEventType, type SyncPayload } from '@/lib/realtimeSync';
import { useAuth } from '@/contexts/AuthContext';

interface RealtimeContextValue {
  channel: RealtimeChannel | null;
  isConnected: boolean;
  subscribe: <T>(event: SyncEventType, handler: (payload: SyncPayload<T>) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue>({
  channel: null,
  isConnected: false,
  subscribe: () => () => {},
});

export const useRealtime = () => useContext(RealtimeContext);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { company, user } = useAuth();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  // Map of event → set of handlers
  const handlersRef = useRef<Map<string, Set<(p: SyncPayload<any>) => void>>>(new Map());

  useEffect(() => {
    if (!company?.id) return;

    const ch = createCompanyChannel(company.id);
    channelRef.current = ch;

    // Listen for all broadcast events and dispatch to registered handlers
    ch.on('broadcast', { event: '*' }, (msg) => {
      const event = msg.event as SyncEventType;
      const payload: SyncPayload<unknown> = {
        event,
        data: msg.payload?.data,
        senderId: msg.payload?.senderId ?? '',
      };
      const handlers = handlersRef.current.get(event);
      if (handlers) {
        handlers.forEach((fn) => fn(payload));
      }
    });

    ch.subscribe((status) => {
      setIsConnected(status === 'SUBSCRIBED');
    });

    return () => {
      ch.unsubscribe();
      channelRef.current = null;
      setIsConnected(false);
    };
  }, [company?.id]);

  function subscribe<T>(
    event: SyncEventType,
    handler: (payload: SyncPayload<T>) => void
  ): () => void {
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as any);
    return () => {
      handlersRef.current.get(event)?.delete(handler as any);
    };
  }

  return (
    <RealtimeContext.Provider
      value={{ channel: channelRef.current, isConnected, subscribe }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}
